import { router } from "@inertiajs/react";
import { useEffect, useState } from "react";
import DomainPagePickerDialog, { majorityProbing } from "@/components/domains/DomainPagePickerDialog";
import MonitoredJourneyChecklist from "@/components/domains/MonitoredJourneyChecklist";
import type { DiscoverablePage, JourneyRow } from "@/types";

const NO_JOURNEYS: JourneyRow[] = [];

type Props = {
  open: boolean;
  onClose: () => void;
  domainId: string;
  pages: DiscoverablePage[];
  journeys?: JourneyRow[];
};

export default function ManageMonitoredPagesDialog({ open, onClose, domainId, pages, journeys = NO_JOURNEYS }: Props) {
  const [selectedJourneyIds, setSelectedJourneyIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [probingById, setProbingById] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedJourneyIds(new Set(journeys.filter((journey) => journey.monitored).map((journey) => journey.id)));
    setSelectedIds(new Set(pages.filter((page) => page.monitored).map((page) => page.id)));
    setProbingById(
      Object.fromEntries(
        pages.map((page) => [
          page.id,
          page.monitored
            ? page.full_application_probing
            : majorityProbing(pages.filter((entry) => entry.id !== page.id)),
        ]),
      ),
    );
  }, [open, pages, journeys]);

  const toggleJourney = (journeyId: string) => {
    setSelectedJourneyIds((current) => {
      const next = new Set(current);
      if (next.has(journeyId)) {
        next.delete(journeyId);
      } else {
        next.add(journeyId);
      }
      return next;
    });
  };

  const togglePage = (pageId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  };

  const save = () => {
    if (submitting) {
      return;
    }

    const pageIds = Array.from(selectedIds);
    const pagesPayload = Object.fromEntries(
      pageIds.map((pageId) => [pageId, { full_application_probing: probingById[pageId] ?? false }]),
    );

    setSubmitting(true);
    router.put(
      `/domains/${domainId}/monitored_pages`,
      {
        scenario_ids: Array.from(selectedJourneyIds),
        page_ids: pageIds,
        pages: pagesPayload,
      },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
        onSuccess: onClose,
      },
    );
  };

  const journeyCount = selectedJourneyIds.size;
  const pageCount = selectedIds.size;
  const selectedSummary =
    journeys.length === 0
      ? `${pageCount} ${pageCount === 1 ? "page" : "pages"} selected for weekly monitoring`
      : `${journeyCount} ${journeyCount === 1 ? "journey" : "journeys"} and ${pageCount} ${pageCount === 1 ? "page" : "pages"} selected for weekly monitoring`;

  return (
    <DomainPagePickerDialog
      open={open}
      onClose={onClose}
      pages={pages}
      canClose
      title="Manage monitoring"
      selectedSummary={selectedSummary}
      closeAriaLabel="Close manage monitoring dialog"
      checkboxAriaLabel={(page) => `Monitor ${page.title || page.path}`}
      selectedIds={selectedIds}
      probingById={probingById}
      onTogglePage={togglePage}
      onProbingChange={(pageId, value) => setProbingById((current) => ({ ...current, [pageId]: value }))}
      needsSelection={false}
      hint="Confirm to save your monitored set."
      submitting={submitting}
      submitLabel={submitting ? "Saving…" : "Confirm"}
      onConfirm={save}
      leading={
        journeys.length > 0 ? (
          <MonitoredJourneyChecklist journeys={journeys} selectedIds={selectedJourneyIds} onToggle={toggleJourney} />
        ) : undefined
      }
    />
  );
}
